// MARK://dhk8/admin-shell/lib/clients/suppliers
// Proveedores → dhk8-erp-suppliers.
//   GET    /api/erp/suppliers?tenantId=...        → { items: ApiSupplier[], total, page, limit }
//   GET    /api/erp/suppliers/:id                 → ApiSupplier
//   POST   /api/erp/suppliers   body { tenantId, legalName, rfc?, type?, networkId? }   (lecturas legacy)
//   PATCH  /api/erp/suppliers/:id  body { legalName?, rfc?, type?, active? }                (lecturas legacy)
//   DELETE /api/erp/suppliers/:id                 (soft delete)
//
// F8.x / ADR-0009: las ESCRITURAS (create/update/delete) ahora van por el grafo
// (erp-suppliers mutations, tenant del JWT, RLS). El tenantId NO se manda en el
// input — lo pone el JWT. Las lecturas list/get ya estaban en el grafo.
//
// CONTRACT NOTE: este servicio expone sus rutas bajo `/api/erp/...` (NO `/v1/...`).
// El ingress mapea `/v1/dhk8-erp-suppliers/(.*)` → `/(.*)` en el pod, así que
// svcUrl('dhk8-erp-suppliers', '/api/erp/suppliers') resuelve correcto. Las
// respuestas vienen envueltas en el sobre `{ ok, data }` → usar apiData.
import { svcUrl, apiData, requireTenant } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import { AdminSuppliersQuery, AdminSupplierQuery, AdminCreateSupplierMutation, AdminUpdateSupplierMutation, AdminDeleteSupplierMutation, AdminCreateSupplierAddressMutation, AdminUpdateSupplierAddressMutation, AdminDeleteSupplierAddressMutation, AdminSupplierItemsQuery, AdminUpsertSupplierItemMutation, AdminDeleteSupplierItemMutation } from '../queries/suppliers.queries.js'

const SUPPLIERS_SVC = 'dhk8-erp-suppliers'

export type SupplierType = 'INDIVIDUAL' | 'COMPANY' | 'FOREIGN'

export interface SupplierAddress {
  id: string
  type: string
  line1: string
  line2: string | null
  city: string
  state: string | null
  zip: string | null
  country: string
}

export interface Supplier {
  id: string
  tenantId: string
  networkId: string | null
  legalName: string
  rfc: string | null
  type: SupplierType
  active: boolean
  archived: boolean
  tags: string[]
  addresses: SupplierAddress[]
  createdAt: string
  updatedAt: string
}


export interface CreateSupplierInput {
  legalName: string
  rfc?: string
  type?: SupplierType
  networkId?: string
}
export interface UpdateSupplierInput {
  legalName?: string
  rfc?: string | null
  type?: SupplierType
  active?: boolean
}

const SUPPLIER_TYPE_LABEL: Record<SupplierType, string> = {
  INDIVIDUAL: 'Persona física',
  COMPANY: 'Persona moral',
  FOREIGN: 'Extranjero',
}
export function supplierTypeLabel(t: SupplierType): string {
  return SUPPLIER_TYPE_LABEL[t] ?? t
}

// ── preview store ──────────────────────────────────────────────────────────
const db: { suppliers: Supplier[] } = {
  suppliers: [
    {
      id: 'sup-demo-1', tenantId: 'tnt_demo', networkId: null,
      legalName: 'Cafés de Altura del Occidente SA de CV', rfc: 'CAO260514AB1',
      type: 'COMPANY', active: true, archived: false, tags: [], addresses: [],
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    },
  ],
}

// Mapea el Supplier del grafo (typed-document-node) al tipo de UI.
interface GqlSupplier {
  id: string; tenantId: string; networkId?: string | null; legalName: string
  rfc?: string | null; type: string; active: boolean; archived: boolean
  tags: ReadonlyArray<string>
  addresses: ReadonlyArray<{ id: string; type: string; line1: string; line2?: string | null; city: string; state?: string | null; zip?: string | null; country: string }>
  createdAt: string; updatedAt: string
}
function toUiSupplier(s: GqlSupplier): Supplier {
  return {
    id: s.id, tenantId: s.tenantId, networkId: s.networkId ?? null, legalName: s.legalName,
    rfc: s.rfc ?? null, type: s.type as SupplierType, active: s.active, archived: s.archived,
    tags: [...s.tags],
    addresses: s.addresses.map((a) => ({
      id: a.id, type: a.type, line1: a.line1, line2: a.line2 ?? null,
      city: a.city, state: a.state ?? null, zip: a.zip ?? null, country: a.country,
    })),
    createdAt: s.createdAt, updatedAt: s.updatedAt,
  }
}

export async function listSuppliers(signal?: AbortSignal): Promise<Supplier[]> {
  if (PREVIEW_STUB) return mock(db.suppliers)
  if (requireTenant() === null) return []
  // F2.2 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminSuppliersQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.suppliers.items ?? []).map(toUiSupplier)
}

export async function getSupplier(id: string, signal?: AbortSignal): Promise<Supplier> {
  if (PREVIEW_STUB) {
    const s = db.suppliers.find((x) => x.id === id)
    if (!s) throw new Error('Proveedor no encontrado')
    return mock(s)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminSupplierQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const s = res.data?.supplier
  if (!s) throw new Error('Proveedor no encontrado')
  return toUiSupplier(s)
}

export async function createSupplier(input: CreateSupplierInput): Promise<Supplier> {
  if (PREVIEW_STUB) {
    const s: Supplier = {
      id: `sup-${Math.random().toString(36).slice(2, 8)}`, tenantId: 'tnt_demo',
      networkId: input.networkId ?? null, legalName: input.legalName,
      rfc: input.rfc ?? null, type: input.type ?? 'COMPANY', active: true,
      archived: false, tags: [], addresses: [],
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    }
    db.suppliers.unshift(s)
    return mock(s)
  }
  // F8.x: escritura por el grafo. tenantId lo pone el JWT (no va en el input).
  const gqlInput: { legalName: string; rfc?: string; type?: SupplierType; networkId?: string } = { legalName: input.legalName }
  if (input.rfc) gqlInput.rfc = input.rfc
  if (input.type) gqlInput.type = input.type
  if (input.networkId) gqlInput.networkId = input.networkId
  const res = await gqlClient.mutation(AdminCreateSupplierMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const s = res.data?.createSupplier
  if (!s) throw new Error('No se pudo crear el proveedor')
  return s as Supplier
}

export async function updateSupplier(id: string, input: UpdateSupplierInput): Promise<Supplier> {
  if (PREVIEW_STUB) {
    const s = db.suppliers.find((x) => x.id === id)
    if (!s) throw new Error('Proveedor no encontrado')
    Object.assign(s, input, { updatedAt: new Date().toISOString() })
    return mock(s)
  }
  // F8.x: escritura por el grafo (tenant del JWT, IDOR-guard por RLS).
  const gqlInput: { legalName?: string; rfc?: string | null; type?: SupplierType; active?: boolean } = {}
  if (input.legalName !== undefined) gqlInput.legalName = input.legalName
  if (input.rfc !== undefined) gqlInput.rfc = input.rfc
  if (input.type !== undefined) gqlInput.type = input.type
  if (input.active !== undefined) gqlInput.active = input.active
  const res = await gqlClient.mutation(AdminUpdateSupplierMutation, { id, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const s = res.data?.updateSupplier
  if (!s) throw new Error('No se pudo actualizar el proveedor')
  return s as Supplier
}

export async function deleteSupplier(id: string): Promise<void> {
  if (PREVIEW_STUB) { db.suppliers = db.suppliers.filter((x) => x.id !== id); return mock(undefined) }
  // F8.x: soft-delete por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminDeleteSupplierMutation, { id }).toPromise()
  if (res.error) throw res.error
}

// ── F8.x: direcciones de proveedor (alta/edición/borrado por el grafo) ───────
// El input es parcial; en el alta line1/city/country son los datos mínimos
// (el backend valida). country por defecto MX. tenantId/IDOR-guard por el JWT.
export interface SupplierAddressInput {
  type?: string
  line1?: string
  line2?: string | null
  city?: string
  state?: string | null
  zip?: string | null
  country?: string
}

interface GqlSupplierAddress { id: string; type: string; line1: string; line2?: string | null; city: string; state?: string | null; zip?: string | null; country: string }
function toUiAddress(a: GqlSupplierAddress): SupplierAddress {
  return { id: a.id, type: a.type, line1: a.line1, line2: a.line2 ?? null, city: a.city, state: a.state ?? null, zip: a.zip ?? null, country: a.country }
}
function toGqlAddressInput(input: SupplierAddressInput): SupplierAddressInput {
  const gqlInput: SupplierAddressInput = {}
  if (input.type !== undefined) gqlInput.type = input.type
  if (input.line1 !== undefined) gqlInput.line1 = input.line1
  if (input.line2 !== undefined) gqlInput.line2 = input.line2
  if (input.city !== undefined) gqlInput.city = input.city
  if (input.state !== undefined) gqlInput.state = input.state
  if (input.zip !== undefined) gqlInput.zip = input.zip
  if (input.country !== undefined) gqlInput.country = input.country
  return gqlInput
}

function previewAddress(input: SupplierAddressInput, id?: string): SupplierAddress {
  return {
    id: id ?? `addr-${Math.random().toString(36).slice(2, 8)}`,
    type: input.type ?? 'FISCAL', line1: input.line1 ?? '', line2: input.line2 ?? null,
    city: input.city ?? '', state: input.state ?? null, zip: input.zip ?? null, country: input.country ?? 'MX',
  }
}

export async function createSupplierAddress(supplierId: string, input: SupplierAddressInput): Promise<SupplierAddress> {
  if (PREVIEW_STUB) {
    const s = db.suppliers.find((x) => x.id === supplierId)
    const a = previewAddress(input)
    if (s) { s.addresses.push(a); s.updatedAt = new Date().toISOString() }
    return mock(a)
  }
  // F8.x: alta por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminCreateSupplierAddressMutation, { supplierId, input: toGqlAddressInput(input) }).toPromise()
  if (res.error) throw res.error
  const a = res.data?.createSupplierAddress
  if (!a) throw new Error('No se pudo crear la dirección')
  return toUiAddress(a)
}

export async function updateSupplierAddress(id: string, input: SupplierAddressInput): Promise<SupplierAddress> {
  if (PREVIEW_STUB) {
    for (const s of db.suppliers) {
      const a = s.addresses.find((x) => x.id === id)
      if (a) { Object.assign(a, toGqlAddressInput(input)); s.updatedAt = new Date().toISOString(); return mock(a) }
    }
    return mock(previewAddress(input, id))
  }
  // F8.x: edición por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminUpdateSupplierAddressMutation, { id, input: toGqlAddressInput(input) }).toPromise()
  if (res.error) throw res.error
  const a = res.data?.updateSupplierAddress
  if (!a) throw new Error('No se pudo actualizar la dirección')
  return toUiAddress(a)
}

export async function deleteSupplierAddress(id: string): Promise<void> {
  if (PREVIEW_STUB) {
    for (const s of db.suppliers) {
      const before = s.addresses.length
      s.addresses = s.addresses.filter((x) => x.id !== id)
      if (s.addresses.length !== before) s.updatedAt = new Date().toISOString()
    }
    return mock(undefined)
  }
  // F8.x: borrado por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminDeleteSupplierAddressMutation, { id }).toPromise()
  if (res.error) throw res.error
}

// ── B: catálogo de compra (SupplierItem) ────────────────────────────────────
// Qué insumo le compras a qué proveedor, en qué presentación y a qué precio.
// Lectura + upsert/borrado por el grafo (erp-suppliers, tenant del JWT, RLS).
export interface SupplierItem {
  id: string
  productId: string
  supplierId: string
  supplierAddressId: string | null
  presentationId: string | null
  supplierSku: string | null
  unitCostCents: number
  currency: string
  leadDays: number | null
  isPreferred: boolean
  active: boolean
}
export interface SupplierItemInput {
  id?: string
  productId: string
  supplierId: string
  supplierAddressId?: string | null
  presentationId?: string | null
  supplierSku?: string | null
  unitCostCents?: number
  currency?: string
  leadDays?: number | null
  isPreferred?: boolean
}

interface GqlSupplierItem {
  id: string; productId: string; supplierId: string
  supplierAddressId?: string | null; presentationId?: string | null
  supplierSku?: string | null; unitCostCents: number; currency: string
  leadDays?: number | null; isPreferred: boolean; active: boolean
}
function toUiSupplierItem(s: GqlSupplierItem): SupplierItem {
  return {
    id: s.id, productId: s.productId, supplierId: s.supplierId,
    supplierAddressId: s.supplierAddressId ?? null, presentationId: s.presentationId ?? null,
    supplierSku: s.supplierSku ?? null, unitCostCents: s.unitCostCents, currency: s.currency,
    leadDays: s.leadDays ?? null, isPreferred: s.isPreferred, active: s.active,
  }
}

export async function listSupplierItems(productId: string, signal?: AbortSignal): Promise<SupplierItem[]> {
  if (PREVIEW_STUB) return mock([] as SupplierItem[])
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminSupplierItemsQuery, { productId }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.supplierItems ?? []).map(toUiSupplierItem)
}

export async function upsertSupplierItem(input: SupplierItemInput): Promise<SupplierItem> {
  if (PREVIEW_STUB) {
    return mock({
      id: input.id ?? `si-${Math.random().toString(36).slice(2, 8)}`,
      productId: input.productId, supplierId: input.supplierId,
      supplierAddressId: input.supplierAddressId ?? null, presentationId: input.presentationId ?? null,
      supplierSku: input.supplierSku ?? null, unitCostCents: input.unitCostCents ?? 0,
      currency: input.currency ?? 'MXN', leadDays: input.leadDays ?? null,
      isPreferred: input.isPreferred ?? false, active: true,
    })
  }
  const gqlInput: { id?: string; productId: string; supplierId: string; supplierAddressId?: string; presentationId?: string; supplierSku?: string; unitCostCents?: number; currency?: string; leadDays?: number; isPreferred?: boolean } = {
    productId: input.productId, supplierId: input.supplierId,
  }
  if (input.id) gqlInput.id = input.id
  if (input.supplierAddressId) gqlInput.supplierAddressId = input.supplierAddressId
  if (input.presentationId) gqlInput.presentationId = input.presentationId
  if (input.supplierSku) gqlInput.supplierSku = input.supplierSku
  if (input.unitCostCents != null) gqlInput.unitCostCents = Math.max(0, Math.round(input.unitCostCents))
  if (input.currency) gqlInput.currency = input.currency
  if (input.leadDays != null) gqlInput.leadDays = input.leadDays
  if (input.isPreferred !== undefined) gqlInput.isPreferred = input.isPreferred
  const res = await gqlClient.mutation(AdminUpsertSupplierItemMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const s = res.data?.upsertSupplierItem
  if (!s) throw new Error('No se pudo guardar el proveedor del insumo')
  return toUiSupplierItem(s)
}

export async function deleteSupplierItem(id: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const res = await gqlClient.mutation(AdminDeleteSupplierItemMutation, { id }).toPromise()
  if (res.error) throw res.error
}
